/**
 * MyListings — Sprint AgentSync SC-015
 *
 * Authenticated page showing only the calling agent's Property24 listings.
 * Calls GET /my-listings (JWT required).
 * Admins see all listings.
 * Each card has a "Manage in Pipeline" CTA that navigates to the Deal Pipeline.
 */

import { useState, useEffect } from 'react';
import { Loader2, RefreshCw, TrendingUp, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { PropertyCard } from './PropertyCard';
import { NewDealModal } from './NewDealModal';
import { Property } from '../types/property';
import { Deal } from '../types/deal';
import apiClient from '@/services/api-client';
import { logger } from '../services/logger.service';

interface Pagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface MyListingsProps {
  onPropertySelect?: (property: Property) => void;
  onDealCreated?: (deal: Deal) => void;
}

function normalizeToProperty(l: any): Property {
  return {
    id: l.somethingPropertyId || l.id || String(l.listingId || Math.random()),
    slug: l.somethingPropertyId || l.id || '',
    title: l.title || 'Untitled Property',
    type: (l.propertyType || 'House') as Property['type'],
    status: 'Published' as Property['status'],
    listingStatus: l.status as Property['listingStatus'],
    somethingPropertyId: l.somethingPropertyId,
    address: {
      street: typeof l.location === 'string' ? l.location : l.location?.address || '',
      suburb: '', city: '', province: '', postalCode: '',
    },
    geo: { lat: 0, lng: 0 },
    price: l.price || 0,
    facts: { bedrooms: l.bedrooms, bathrooms: l.bathrooms, floorSize: l.squareMeters },
    amenities: [],
    media: (l.images || []).map((img: any, i: number) => ({
      id: `img-${i}`, url: typeof img === 'string' ? img : img?.url || '',
      alt: l.title || '', isCover: i === 0, order: i,
    })),
    description: l.description || '',
    listingDate: new Date(l.createdAt || Date.now()).toISOString(),
    agentId: l.agentId || '',
    agent: l.agentName ? {
      id: l.agentId || '', name: l.agentName, email: l.agentEmail || '',
      phone: l.agentPhone || '', whatsapp: l.agentPhone || '',
      avatarUrl: l.agentPhotoUrl || undefined,
    } : undefined,
    visibility: 'Public' as Property['visibility'],
    flags: {},
    mandateType: l.mandateType,
  };
}

export function MyListings({ onPropertySelect, onDealCreated }: MyListingsProps) {
  const [listings, setListings] = useState<any[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1, pageSize: 30, totalItems: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [newDealModal, setNewDealModal] = useState<{ open: boolean; prefill: any }>({ open: false, prefill: null });

  const loadListings = async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const raw = await apiClient.get<any>(`/my-listings?page=${page}&pageSize=30`);
      // API returns { success: true, data: { listings, pagination, agentId } }
      const data = raw?.data ?? raw;
      setListings(data.listings || []);
      setPagination(data.pagination || { page, pageSize: 30, totalItems: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false });
    } catch (err: any) {
      logger.error('Failed to load my listings', err as Error, undefined, 'MyListings');
      setError('Failed to load your listings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadListings(currentPage);
  }, [currentPage]);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm">Loading your listings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" onClick={() => loadListings(currentPage)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">My Listings</h2>
          <p className="text-muted-foreground text-sm mt-1">
            {pagination.totalItems > 0
              ? `${pagination.totalItems} listing${pagination.totalItems !== 1 ? 's' : ''} from Property24`
              : 'No listings assigned to your profile yet'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadListings(currentPage)}
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Empty state */}
      {listings.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-muted-foreground">
          <Building2 className="w-12 h-12 opacity-30" />
          <div className="text-center">
            <p className="font-medium">No listings found</p>
            <p className="text-sm mt-1">Your Property24 listings will appear here after the next sync.</p>
          </div>
        </div>
      )}

      {/* Listings grid */}
      {listings.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {listings.map((listing, idx) => {
            const property = normalizeToProperty(listing);
            return (
              <div key={property.id || idx} className="relative group">
                <PropertyCard
                  property={property}
                  onClick={() => onPropertySelect?.(property)}
                />
                {/* Start Deal CTA — overlays the bottom of the card on hover */}
                <div className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <Button
                    className="w-full rounded-t-none rounded-b-md h-9 text-xs font-medium bg-primary hover:bg-primary/90"
                    onClick={e => {
                      e.stopPropagation();
                      setNewDealModal({
                        open: true,
                        prefill: {
                          listingId: listing.id || listing.somethingPropertyId,
                          propertyTitle: listing.title || property.title,
                          propertyAddress: typeof listing.location === 'string' ? listing.location : listing.location?.address || '',
                          listingPhotoUrl: listing.images?.[0]?.url || (typeof listing.images?.[0] === 'string' ? listing.images[0] : ''),
                        },
                      });
                    }}
                  >
                    <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
                    Start Deal
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(currentPage - 1)}
            disabled={!pagination.hasPrevPage}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground px-3">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(currentPage + 1)}
            disabled={!pagination.hasNextPage}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
      {/* New Deal Modal — pre-filled from listing (SC-016) */}
      <NewDealModal
        open={newDealModal.open}
        onOpenChange={open => setNewDealModal(prev => ({ ...prev, open }))}
        onDealCreated={deal => {
          setNewDealModal({ open: false, prefill: null });
          onDealCreated?.(deal);
        }}
        prefill={newDealModal.prefill}
      />
    </div>
  );
}
